import { useEffect, useState } from 'react';

// Blurred-background "we're locking this in" curtain that runs while the
// booking insert is in flight. Each step lingers ~700ms so the whole thing
// reads as quick choreography rather than a real progress bar.
export type CheckoutModalProps = {
  open: boolean;
  vendorName: string;
  payNow: boolean;
  // The booking insert resolved successfully. The modal will finish its
  // current step, show "All set!", then call onFinished so the parent can
  // navigate to the success page.
  done: boolean;
  onFinished: () => void;
};

export default function CheckoutModal({ open, vendorName, payNow, done, onFinished }: CheckoutModalProps) {
  const steps = [
    `Confirming with ${vendorName}`,
    'Reserving your spot',
    'Pouring drinks',
    ...(payNow ? ['Checking payment details'] : []),
  ];
  const [stepIdx, setStepIdx] = useState(0);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (!open) {
      setStepIdx(0);
      setFinishing(false);
      return;
    }
    const id = window.setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, steps.length - 1));
    }, 700);
    return () => window.clearInterval(id);
    // steps.length is derived from props and stable within an open cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !done) return;
    // Snap to the last step, hold a beat on "All set!", then let the parent
    // navigate. The transition feels confident instead of jarring.
    // NOTE: do not depend on `finishing` here — toggling it would re-run this
    // effect, which would clear the navigation timeout via the cleanup and
    // leave the modal stuck on "All set!".
    setFinishing(true);
    setStepIdx(steps.length - 1);
    const id = window.setTimeout(() => onFinished(), 650);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, done]);

  if (!open) return null;

  const label = finishing ? 'All set!' : steps[stepIdx];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 400,
        background: 'rgba(20, 16, 14, 0.45)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'ga-fade-in 0.25s ease',
      }}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          borderRadius: 22,
          padding: '36px 32px',
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.45)',
          animation: 'ga-pop-in 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
        }}
      >
        <Spinner finishing={finishing} />
        <div
          className="display"
          style={{ fontSize: 22, marginTop: 22, minHeight: 30 }}
          key={label}
        >
          <span style={{ display: 'inline-block', animation: 'ga-step-in 0.35s ease' }}>{label}…</span>
        </div>
      </div>
      <style>{`
        @keyframes ga-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ga-pop-in {
          0%   { opacity: 0; transform: translateY(12px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ga-step-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ga-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes ga-pop-check {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function Spinner({ finishing }: { finishing: boolean }) {
  if (finishing) {
    return (
      <div
        style={{
          width: 56,
          height: 56,
          margin: '0 auto',
          borderRadius: 999,
          background: 'var(--wine)',
          color: 'var(--bg)',
          display: 'grid',
          placeItems: 'center',
          animation: 'ga-pop-check 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.4)',
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="5 12 10 17 19 7" />
        </svg>
      </div>
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: 56,
        height: 56,
        margin: '0 auto',
        borderRadius: 999,
        border: '4px solid var(--line)',
        borderTopColor: 'var(--wine)',
        animation: 'ga-spin 0.9s linear infinite',
      }}
    />
  );
}
